import {NotificationUsersConfirmationValidationSchema} from '@/features/users/types'
import {useTranslation} from '@/translations/i18n'
import {useFormContext, useWatch} from 'react-hook-form'
import {Flexbox} from '../flexbox/FlexBox'
import {ConfirmationDialogAccordionContainer} from './ConfirmationDialogAccordionContainer'
import {
    SelectedItem,
    SelectedItemWithData,
    UserSelectionItem,
    UsersAndGroupsMultiSelectPopover
} from '@/features/users/components/users-and-groups-multi-select-popover/UsersAndGroupsMultiSelectPopover'
import {CombinedItem} from '@/features/users/hooks/useUsersAndGroupsSearchableList'
import {Button} from '../button/Button'
import {PlusIcon, XCloseIcon} from '../icon/Icon'
import {Badge} from '../badge/Badge'
import {Avatar} from '../avatar/Avatar'
import {Text} from '../text/Text'
import {TextArea} from '@/components/commons/textarea/TextArea'

export const ConfirmationDialogAccordionNotification = () => {
    const {t} = useTranslation()
    const form = useFormContext<NotificationUsersConfirmationValidationSchema>()
    const selectedUsers = useWatch({control: form.control, name: 'notificationsRecipients'})

    const selectedItems: SelectedItem[] = selectedUsers
        ? selectedUsers.map(user => ({
              type: 'user' as const,
              id: user.id
          }))
        : []

    const currentDisplayItems: SelectedItemWithData[] = selectedUsers
        ? selectedUsers.map(user => ({
              type: 'user' as const,
              id: user.id,
              name: user.fullName
          }))
        : []

    const handleParticipantToggle = (users: UserSelectionItem[], itemData: CombinedItem): void => {
        const currentIds = selectedUsers?.map(u => u.id) || []

        if (itemData.type === 'group') {
            const memberIds = users.map(u => u.id)
            const allMembersSelected = memberIds.every(id => currentIds.includes(id))

            if (allMembersSelected) {
                const updatedRecipients = selectedUsers?.filter(u => !memberIds.includes(u.id)) || []
                form.setValue('notificationsRecipients', updatedRecipients)
            } else {
                const newMembers =
                    itemData.members
                        ?.filter(member => !currentIds.includes(member.id))
                        .map(member => ({
                            id: member.id,
                            fullName: member.fullName
                        })) || []
                form.setValue('notificationsRecipients', [...(selectedUsers || []), ...newMembers])
            }
        } else {
            const userId = users[0]?.id
            if (!userId) return

            if (currentIds.includes(userId)) {
                form.setValue('notificationsRecipients', selectedUsers?.filter(u => u.id !== userId) || [])
            } else {
                const newUser = {
                    id: userId,
                    fullName: itemData.fullName ?? itemData.name
                }
                form.setValue('notificationsRecipients', [...(selectedUsers || []), newUser])
            }
        }
    }

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            <ConfirmationDialogAccordionContainer gap={2} align="center">
                <UsersAndGroupsMultiSelectPopover
                    selectedItems={selectedItems}
                    currentDisplayItems={currentDisplayItems}
                    toggleClick={handleParticipantToggle}
                    trigger={
                        <Button variant="primaryNeutral" shape="square" size="sm">
                            <PlusIcon />
                        </Button>
                    }
                />

                {selectedUsers && selectedUsers.length > 0 ? (
                    selectedUsers.map(user => (
                        <Badge size="sm" shape="rectangle" key={user.id}>
                            <Avatar size="sm" name={user.fullName} />
                            {user.fullName}
                            <Button
                                shape="text"
                                variant="ghost"
                                onClick={() => {
                                    form.setValue(
                                        'notificationsRecipients',
                                        selectedUsers.filter(u => u.id != user.id)
                                    )
                                }}
                            >
                                <XCloseIcon />
                            </Button>
                        </Badge>
                    ))
                ) : (
                    <Text size="textSm" palette="neutral" shade="700">
                        {t('commons:select_consultants')}
                    </Text>
                )}
            </ConfirmationDialogAccordionContainer>
            <TextArea placeholder={t('users:notes_placeholder')} {...form.register('notificationDetails')} />
        </Flexbox>
    )
}
